// Global Instructions Rule Applied!
//
// How an agent proposal READS and what you can DO with it — shared by the two
// screens that show proposals:
//   * My Agents → Agent activity (ProposalsTab)      — the user's own rows.
//   * Agent Oversight → Recent activity (admin)      — every user's rows.
//
// WHY THIS FILE EXISTS. The admin screen used to be view-only and carried its
// own copy of the status vocabulary, with a comment promising it matched the
// user tab. Now that an admin can review, approve, reject, retry and undo,
// the two screens must agree about far more than a Tag colour — which verb a
// row offers, what its outcome lines say, where Open goes. One copy, so the
// same event cannot read two ways.
//
// WHO ACTIONED IT (2026-09-02). Every terminal state names a human, because
// on either screen the actor may not be the reader:
//   * approved_by — whose session COMMITTED the write. Set at the approve /
//     retry claim and never overwritten, so "Approved by Ada" survives
//     "Undone by Owen".
//   * reviewed_by — the LAST verdict (approve, reject or undo).
// Both arrive null until there is somebody to name.
import React from 'react'
import { Button, Popconfirm, Tag, Typography } from 'antd'
import type { AgentProposal } from '../../../state_manager/data_models/agent_access_model.ts'
import { ENTITY_OPEN_TARGET, TOOL_TO_ENTITY, interactionParentRef } from './proposalForms.ts'

const { Text } = Typography

/** Either screen's row: the owner's proposal, or the admin's (same + user). */
export type ProposalRow = AgentProposal & {
  prog__user_name?: string | null
  prog__user_is_me?: boolean
}

/**
 * How to name the human behind an action.
 *
 * 'you' when it was the reader — the phrasing the owner screen has always
 * used, and still correct for an admin acting on their own row. Otherwise the
 * resolved name. 'someone else' only when an action happened but the name
 * could not be resolved (a deleted auth user), which is still more honest
 * than silently dropping the clause and implying the reader did it.
 */
export const whoLabel = (name: string | null | undefined, isMe: boolean | undefined): string =>
  isMe ? 'you' : name || 'someone else'

/**
 * The row's status Tag.
 *
 * Colours follow the screen's established scheme: pending = blue,
 * applied/success = green, failed = red, approved (replay in flight or
 * interrupted) = orange, terminal-but-nothing-created = default.
 */
export const statusTag = (proposal: ProposalRow): React.ReactElement => {
  switch (proposal.agent_proposals__status) {
    case 'pending':
      return <Tag color='blue'>Pending review</Tag>
    case 'applied':
      // A live write and an approved one are both "applied", but they are NOT
      // the same event — say which happened, and by whom.
      return proposal.agent_proposals__live ? (
        <Tag color='green'>Created live</Tag>
      ) : (
        <Tag color='green'>
          Approved by {whoLabel(proposal.prog__approved_by_name, proposal.prog__approved_by_is_me)}
        </Tag>
      )
    case 'failed':
      return <Tag color='red'>Failed</Tag>
    case 'approved':
      return <Tag color='orange'>Approved</Tag>
    case 'rejected':
      // Rejected rows are listed since 2026-09-02 (reject no longer trashes).
      // Naming the rejector is the whole reason they are visible: an admin can
      // decline on your behalf, and a proposal that simply vanished told you
      // nothing.
      return (
        <Tag>
          Rejected by {whoLabel(proposal.prog__reviewed_by_name, proposal.prog__reviewed_by_is_me)}
        </Tag>
      )
    case 'undone':
      return (
        <Tag>
          Undone by {whoLabel(proposal.prog__reviewed_by_name, proposal.prog__reviewed_by_is_me)}
        </Tag>
      )
    default:
      return <Tag>{proposal.agent_proposals__status}</Tag>
  }
}

/**
 * The "this was edited before approval" marker, or null.
 *
 * Reference information, not a status — so it sits with the row's identity, in
 * the purple the app reserves for provenance-ish markers, and never replaces
 * the status Tag. Driven by original_payload being non-null, which IS the
 * amended flag (there is no second column to fall out of step).
 *
 * The editor is the approver. On a row undone before approved_by existed there
 * is no name to give, so it degrades to a bare "Edited" rather than borrowing
 * reviewed_by, which by then names whoever undid it.
 */
export const editedTag = (proposal: ProposalRow): React.ReactElement | null => {
  if (!proposal.agent_proposals__original_payload) return null
  const editor = proposal.prog__approved_by_is_me
    ? 'you'
    : proposal.prog__approved_by_name
  return (
    <Tag color='purple' style={{ marginLeft: 8 }}>
      {editor ? `Edited by ${editor}` : 'Edited'}
    </Tag>
  )
}

export type OutcomeLine = { text: string; type: 'danger' | 'warning' | 'secondary' }

const resultText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '')

/**
 * What the reader needs to know about a finalized row, beyond the tag: the
 * failure sentence (never blank — an empty `error` is itself reported), the
 * duplicate values behind a DuplicateError, task failures that rode on an
 * otherwise-applied deal, a stuck mid-replay approval, a refused or partial
 * undo. The raw result JSON is in the expanded row; these are the lines worth
 * reading without expanding.
 */
export const outcomeLines = (proposal: ProposalRow): OutcomeLine[] => {
  const result = proposal.agent_proposals__result || {}
  const lines: OutcomeLine[] = []
  switch (proposal.agent_proposals__status) {
    case 'failed': {
      const error = resultText(result.error)
      lines.push({
        text: error || 'Failed — no error text was recorded (check the server log for this proposal id)',
        type: 'danger'
      })
      // The backend's DuplicateError sentence already names the values; the
      // per-key lines only add information when it does not (older rows).
      const duplicates = result.duplicates
      if (duplicates && typeof duplicates === 'object') {
        Object.entries(duplicates as Record<string, unknown>).forEach(([key, values]) => {
          if (Array.isArray(values) && values.length > 0 && !values.every((v) => error.includes(String(v)))) {
            lines.push({
              text: `Already exists — ${key.replace('__', ' ')}: ${values.map(String).join(', ')}`,
              type: 'secondary'
            })
          }
        })
      }
      // error_type stays in the Outcome JSON of the expanded row — a class
      // name is for the log reader, not the reviewer.
      break
    }
    case 'applied': {
      const taskErrors = result.tasks__errors
      if (Array.isArray(taskErrors)) {
        taskErrors.forEach((entry) => lines.push({ text: `Task not created: ${String(entry)}`, type: 'warning' }))
      }
      break
    }
    case 'approved':
      lines.push({
        text: 'Approval started but never finished — the backend was interrupted mid-replay. Check whether the record exists before asking the agent to file it again.',
        type: 'secondary'
      })
      break
    case 'undone': {
      // Written by the backend through agent_record_undo_outcome: undo_error
      // when the primary record was refused or would not delete (it is still
      // there), undo_residue for cluster members left in place.
      const undoError = resultText(result.undo_error)
      if (undoError) lines.push({ text: `Undo problem: ${undoError}`, type: 'danger' })
      const residue = result.undo_residue
      if (Array.isArray(residue)) {
        residue.forEach((entry) => lines.push({ text: `Left in place: ${String(entry)}`, type: 'warning' }))
      }
      break
    }
    default:
      break
  }
  return lines
}

/**
 * Where an APPLIED proposal's Open button goes.
 *
 * Four of the five tools open the record they created, by its PRIMARY id only.
 * A create_prospect result also carries `accounts__id`, which is the joined
 * investor account whether or not this write created it — opening that could
 * land the reader on a company the agent never touched.
 *
 * The fifth, log_interaction, has no record of its own to open, so it opens the
 * parent with the interaction named in the query.
 *
 * Returns null when there is nothing to open — a result that recorded no id, or
 * an interaction with no parent — so the button simply does not render rather
 * than offering a link to nowhere.
 */
export const openTargetFor = (
  proposal: ProposalRow
): { module: string; id: string; query?: Record<string, string> } | null => {
  const entity = TOOL_TO_ENTITY[proposal.agent_proposals__tool]
  if (!entity) return null
  const result = (proposal.agent_proposals__result ?? null) as Record<string, unknown> | null

  // An interaction has no form of its own — it lives in its parent's timeline.
  // So Open lands on the PARENT (always present: log_interaction refuses to
  // build without one) and names the interaction in the query, which the
  // timeline picks up and opens exactly as a click on the card would.
  if (entity === 'interaction') {
    const parent = interactionParentRef(proposal.agent_proposals__payload as Record<string, any> | null)
    if (!parent) return null
    const interactionId = result?.interactions__id
    return {
      ...parent,
      ...(typeof interactionId === 'string' && interactionId
        ? { query: { interaction: interactionId } }
        : {})
    }
  }

  const target = ENTITY_OPEN_TARGET[entity]
  if (!target) return null
  const id = result?.[target.resultKey]
  return typeof id === 'string' && id ? { module: target.module, id } : null
}

export interface ProposalActionsProps {
  proposal: ProposalRow
  busy: boolean
  /** Open the proposal in the New form it is destined for. */
  onReview: (proposal: ProposalRow) => void
  /** Reject outright — nothing is created; the row stays, marked rejected. */
  onReject: (proposalId: string) => void
  /** Reverse an applied write through the module's own delete path. */
  onUndo: (proposalId: string) => void
  onOpen: (target: { module: string; id: string; query?: Record<string, string> }) => void
}

/**
 * ONE verb slot, driven by status (owner 2026-09-02):
 *   pending  → Review · Reject
 *   failed   → Review & retry
 *   applied  → Open · Undo
 *   rejected / undone → nothing. Undo DELETES the created record, so an undone
 *     row has nothing to open; rejected never created anything. An undone row
 *     whose undo was refused or partial says so in its outcome lines
 *     (result.undo_error / undo_residue), not via a button.
 *
 * Identical on both screens by design: an admin assisting a user is doing the
 * same thing the user would do, so it must not look like a different feature.
 */
export function ProposalActions({
  proposal,
  busy,
  onReview,
  onReject,
  onUndo,
  onOpen
}: ProposalActionsProps): React.ReactElement | null {
  const status = proposal.agent_proposals__status

  if (status === 'applied') {
    const target = openTargetFor(proposal)
    return (
      <>
        {target ? (
          <Button size='small' onClick={() => onOpen(target)}>
            Open
          </Button>
        ) : null}
        {/* Applied — live or approved — can be reversed. This is what makes
            live writes safe to hand an agent: nothing it creates is permanent
            without the user's agreement, it is just after the fact rather than
            before it. */}
        <Popconfirm
          title='Undo this?'
          description='The records this created are removed (trashed), exactly as if you deleted them yourself.'
          okText='Undo'
          okButtonProps={{ danger: true }}
          onConfirm={() => onUndo(proposal.agent_proposals__id)}
        >
          <Button danger size='small' loading={busy} style={target ? { marginLeft: 8 } : undefined}>
            Undo
          </Button>
        </Popconfirm>
      </>
    )
  }

  if (status === 'pending') {
    return (
      <>
        {/* No Popconfirm on Review: it opens the proposal in the form it will
            become, which IS the confirmation — a far better one than a
            sentence describing a payload the reader cannot see. */}
        <Button type='primary' size='small' loading={busy} onClick={() => onReview(proposal)}>
          Review
        </Button>
        <Popconfirm
          title='Reject this proposal?'
          description='Nothing is created. The proposal stays in the list, marked rejected, so everyone can see the decision.'
          okText='Reject'
          okButtonProps={{ danger: true }}
          onConfirm={() => onReject(proposal.agent_proposals__id)}
        >
          <Button danger size='small' loading={busy} style={{ marginLeft: 8 }}>
            Reject
          </Button>
        </Popconfirm>
      </>
    )
  }

  if (status === 'failed') {
    // Failed — the replay raised, nothing was created. It goes back through
    // the SAME review form rather than a blind retry: replaying a payload that
    // failed on its own contents fails identically, and the form is where the
    // cause (a missing required field, a wrong lookup) can actually be fixed.
    return (
      <Button type='primary' size='small' loading={busy} onClick={() => onReview(proposal)}>
        Review &amp; retry
      </Button>
    )
  }

  return null
}

/**
 * The expanded row: the EXACT payload an approval would replay — verbatim, not
 * a paraphrase (design §6) — beside what the agent originally filed when a
 * human edited it, and the raw outcome once finalized.
 */
export function ProposalPayloadPanel({ proposal }: { proposal: ProposalRow }): React.ReactElement {
  const editor = proposal.prog__approved_by_is_me
    ? 'you'
    : proposal.prog__approved_by_name
  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
      {/* When the approver edited the proposal, what the AGENT filed is shown
          beside what was actually approved — otherwise the audit trail would
          silently read as the agent's work. */}
      {proposal.agent_proposals__original_payload ? (
        <div style={{ flex: 1, minWidth: 280 }}>
          <Text type='secondary' strong style={{ fontSize: 12 }}>
            Filed by the agent
          </Text>
          <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(proposal.agent_proposals__original_payload, null, 2)}
          </pre>
        </div>
      ) : null}
      <div style={{ flex: 1, minWidth: 280 }}>
        <Text type='secondary' strong style={{ fontSize: 12 }}>
          {proposal.agent_proposals__original_payload
            ? `Approved by ${editor || 'the reviewer'}`
            : 'Payload'}
        </Text>
        <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>
          {JSON.stringify(proposal.agent_proposals__payload, null, 2)}
        </pre>
      </div>
      {proposal.agent_proposals__result ? (
        <div style={{ flex: 1, minWidth: 280 }}>
          <Text type='secondary' strong style={{ fontSize: 12 }}>
            Outcome
          </Text>
          <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(proposal.agent_proposals__result, null, 2)}
          </pre>
        </div>
      ) : null}
    </div>
  )
}
